import { JSX } from "react";
import { useAdCategoryStore } from "../store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import { settingsOptions } from "./step-four";

export default function CreateAdCategoriesStepSix(): JSX.Element {
  const { adCategory, adTypes, parametersInput, customParameters, parametersData } =
    useAdCategoryStore(
      useShallow((state) => ({
        adCategory: state.adCategory,
        adTypes: state.adTypes,
        parametersInput: state.parametersInput,
        customParameters: state.customParameters,
        parametersData: state.parametersData,
      })),
    );

  return (
    <div className="mt-3">
      <h1 className="text-2xl font-bold mb-4">Ad Type Review</h1>
      <h2 className="text-xl font-normal mb-6">
        Set Up Category / {adCategory || "[Category Name]"}
      </h2>
      <hr className="border-[#EA33C0] mb-6" />
      <h3 className="font-bold text-base mb-4">Step 6: Review & Submit</h3>
      <div>
        {adTypes.map((adType, adTypeIndex) => (
          <div key={adTypeIndex}>
            <div className="mb-2 text-sm">
              Publisher:{" "}
              <span className="font-semibold">{adType.publisher}</span>
            </div>
            {adType.adInputs.map((adInput, adInputIndex) => {
              const baseKey = `${adType.publisher}_${adInput.name}`;

              return (
                <div key={adInputIndex}>
                  <div className="mb-2 text-sm">
                    Ad Type:{" "}
                    <span className="font-semibold">{adInput.name}</span>
                  </div>
                  {(parametersData[adInput.mediaType] || []).map((group) => {
                    const parameterKey = `${baseKey}_${group.key}`;
                    const paramObj = parametersInput.find(
                      (p) => p.key === parameterKey,
                    ) || {
                      settings: "",
                      inputs: [{}],
                      enabled: false,
                    };
                    const { settings, inputs, enabled } = paramObj;

                    return (
                      <div className="mt-6" key={group.key}>
                        <div className="text-sm font-semibold mb-1">
                          {group.label}
                        </div>
                        <hr className="mt-2 border-gray-300 mb-3" />

                        <div>
                          {inputs.map((input, inputIndex) => {
                            return (
                              <div className="grid grid-cols-12 gap-6 mb-4">
                                <div className="col-span-3">
                                  {inputIndex == 0 ? (
                                    <>
                                      <div className="text-gray-400 text-sm mb-1">
                                        Setting
                                      </div>
                                      <div className="text-sm">
                                        {settingsOptions.find(
                                          (e) => e.value === settings,
                                        )?.title || ""}
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                                {group.fields.map((field) => {
                                  const value = input[field.key] ?? "";

                                  return (
                                    <div className="col-span-2" key={field.key}>
                                      <div className="text-gray-400 text-sm mb-1">
                                        {field.label}
                                      </div>
                                      <div className="text-sm">{value}</div>
                                    </div>
                                  );
                                })}
                                <div className="col-span-2">
                                  <div className="text-gray-400 text-sm mb-1">
                                    Enable
                                  </div>
                                  <div className="text-sm">
                                    {enabled ? "Yes" : "No"}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {customParameters.filter(
                    (e) =>
                      e.key.split("_")[0] + "_" + e.key.split("_")[1] ==
                      baseKey,
                  ).length > 0 && (
                    <div className="mt-10">
                      <hr className="mt-10 border-1 border-gray-300 mb-8" />
                      <div className="font-semibold mb-2">
                        Custom Parameters
                      </div>
                      {customParameters
                        .filter(
                          (e) =>
                            e.key.split("_")[0] + "_" + e.key.split("_")[1] ==
                            baseKey,
                        )
                        .map((param) => {
                          return (
                            <div key={param.key} className="mb-5">
                              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-2">
                                <div className="w-full md:w-1/3">
                                  <span className="text-gray-500 text-xs">
                                    Label
                                  </span>
                                  <div className="font-medium">
                                    {param.name || (
                                      <span className="italic text-gray-400">
                                        Untitled
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="w-full md:w-1/3">
                                  <span className="text-gray-500 text-xs">
                                    Type
                                  </span>
                                  <div className="text-sm capitalize">
                                    {param.type || "-"}
                                  </div>
                                </div>
                                <div className="w-full md:w-1/3">
                                  <span className="text-gray-500 text-xs">
                                    Setting
                                  </span>
                                  <div className="text-sm">
                                    {settingsOptions.find(
                                      (opt) => opt.value === param.setting,
                                    )?.title || "-"}
                                  </div>
                                </div>
                              </div>
                              {param.type === "dropdown" &&
                                param.dropdownOptions?.length > 0 && (
                                  <div className="mb-2">
                                    <span className="text-gray-500 text-xs">
                                      Options:
                                    </span>
                                    <span className="ml-2 text-sm">
                                      {param.dropdownOptions
                                        .filter(Boolean)
                                        .join(", ")}
                                    </span>
                                  </div>
                                )}

                              <div>
                                <span className="text-gray-500 text-xs">
                                  {param.setting === "multi_publisher"
                                    ? "Values"
                                    : "Value"}
                                  :
                                </span>
                                <span className="ml-2 text-sm">
                                  {param.type === "file_upload" ? (
                                    param.values &&
                                    param.values.length > 0 &&
                                    param.values.some(
                                      (val: any) =>
                                        val instanceof File ||
                                        (typeof val === "string" &&
                                          val.trim() !== ""),
                                    ) ? (
                                      param.values
                                        .filter(
                                          (val: any) =>
                                            val instanceof File ||
                                            (typeof val === "string" &&
                                              val.trim() !== ""),
                                        )
                                        .map((val: any) => {
                                          if (val instanceof File) {
                                            return val.name;
                                          }
                                          return val;
                                        })
                                        .join(", ")
                                    ) : (
                                      <span className="italic text-gray-400">
                                        No file uploaded
                                      </span>
                                    )
                                  ) : param.values &&
                                    param.values.length > 0 ? (
                                    param.values.filter(Boolean).join(", ")
                                  ) : (
                                    <span className="italic text-gray-400">
                                      No value set
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {adInputIndex !== adType.adInputs.length - 1 && (
                    <hr className="mt-10 border-1 border-gray-300 mb-3" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
